if (!customElements.get("google-map")) {
  class GoogleMap extends HTMLElement {
    constructor() {
      super()
      this.container = this.querySelector(".google-map--map")
      this.mapZoom = parseInt(this.container.dataset.mapZoom, 10)
      this.mapStyle = this.container.dataset.mapStyle
      this.mapType = this.container.dataset.mapType
      this.locations = this.querySelectorAll(".store-location")
      this.locationInformations = this.querySelectorAll(".collapsible-row")
      this.details = this.querySelectorAll(".map-accordion details")
      this.map = null
      this.markers = []
      this.infoWindows = []
      this.activeIndex = null
    }

    connectedCallback() {
      if (!window.google) return

      this.bounds = new google.maps.LatLngBounds()
      this.renderMap()

      window.addEventListener("resize", () => {
        this.mapResize(this.map, this.bounds)
      })

      google.maps.event.addListenerOnce(this.map, "idle", () => {
        this.map.setCenter(this.bounds.getCenter())
        this.map.fitBounds(this.bounds)
      })

      // Handle collapsible clicks
      this.details.forEach((targetDetail, i) => {
        targetDetail.addEventListener("toggle", () => {
          if (targetDetail.open) {
            // Close others
            this.details.forEach((detail, idx) => {
              if (idx !== i) detail.removeAttribute("open")
            })
            // Update ARIA
            this.setAriaStates(i)

            // Sync map
            this.highlightMarker(i)
            this.activeIndex = i
            this.map.setCenter(this.markers[i].getPosition())
          }

          // If none are open, clear info/animation and ARIA
          const anyOpen = Array.from(this.details).some(d => d.open)
          if (!anyOpen) {
            this.clearHighlight()
            this.setAriaStates(null)
          }
        })
      })

      // Initialize ARIA based on initial DOM state
      const initiallyOpenIndex = this.getAnyOpenIndex()
      this.setAriaStates(initiallyOpenIndex)

      window.addEventListener("load", () => {
        google.maps.event.trigger(this.map, "resize")
        this.map.setCenter(this.bounds.getCenter())
      })
    }

    // ---- ARIA helpers ----
    getAnyOpenIndex() {
      const idx = Array.from(this.details).findIndex(d => d.open)
      return idx >= 0 ? idx : null
    }

    setAriaStates(openIndex /* number | null */) {
      this.details.forEach((d, idx) => {
        const summary = d.querySelector("summary")
        if (!summary) return
        // exactly one "true" when something is open; all "false" when none are open
        summary.setAttribute("aria-expanded", openIndex !== null && idx === openIndex ? "true" : "false")
      })
    }

    getMapOptions() {
      return {
        zoom: this.mapZoom,
        draggable: true, // Allow dragging on all devices, including mobile
        scrollwheel: false,
        fullscreenControl: false,
        mapTypeId: this.mapType,
        gestureHandling: "greedy", // Enable full gesture handling on mobile
      }
    }

    renderMap() {
      const mapOptions = this.getMapOptions()
      this.map = new google.maps.Map(this.container, mapOptions)

      this.locations.forEach((location, i) => {
        this.renderMarker(i, location)
      })
    }

    renderMarker(i, location) {
      const options = JSON.parse(location.dataset.option)
      const latLng = new google.maps.LatLng(options.latitude, options.longitude)

      const marker = new google.maps.Marker({
        position: latLng,
        map: this.map,
        title: options.title,
        icon: {
          url: options.marker_image,
          scaledSize: new google.maps.Size(30, 45), // Adjust size
        },
      })

      this.markers.push(marker)
      this.bounds.extend(latLng)

      const infoWindow = new google.maps.InfoWindow({
        content: `<div><span class="gm-dialog__title fs-body-100 fs-body-bold">${options.title}</span><p class="gm-dialog__description">${options.description}</p></div>`,
      })
      this.infoWindows.push(infoWindow)

      // Add marker click listener to highlight corresponding collapsible
      marker.addListener("click", () => {
        this.highlightMarker(i)
        this.toggleCollapsible(i)          // open i, close others
        this.setAriaStates(i)              // keep aria in sync for programmatic opens
        this.activeIndex = i
        this.scrollToCollapsible(i)
      })
    }

    highlightMarker(i) {
      // Close all info windows and reset markers
      this.infoWindows.forEach((infoWindow, index) => {
        infoWindow.close()
        this.markers[index].setAnimation(null) // Reset animation
      })

      // Highlight the clicked marker
      this.infoWindows[i].open(this.map, this.markers[i])
      this.markers[i].setAnimation(google.maps.Animation.BOUNCE)
    }

    // Close info windows + stop animations, but DO NOT hide markers
    clearHighlight() {
      this.infoWindows.forEach(iw => iw.close())
      this.markers.forEach(m => m.setAnimation(null))
      this.activeIndex = null
    }

    toggleCollapsible(i) {
      // Close all other collapsibles except the current one; open the selected
      this.locationInformations.forEach((el, index) => {
        const details = el.querySelector("details")
        if (!details) return
        if (index !== i) {
          details.removeAttribute("open")
        } else {
          details.setAttribute("open", "")
        }
      })
    }

    scrollToCollapsible(i) {
      const collapsibleContainer = this.locationInformations[i]?.closest(
        ".google-map--information--inner"
      )
      const detailsElement =
        this.locationInformations[i]?.querySelector("details")

      if (collapsibleContainer && detailsElement) {
        const offsetTop = detailsElement.offsetTop
        collapsibleContainer.scrollTo({
          top: offsetTop - 10, // Add some padding to the scroll
          behavior: "smooth", // Smooth scroll
        })
      }
    }

    mapResize(map, bounds) {
      setTimeout(() => {
        google.maps.event.trigger(map, "resize")
        map.setCenter(bounds.getCenter()) // Recenter the map
        map.fitBounds(bounds) // Fit the map to bounds
      }, 50)
    }
  }

  customElements.define("google-map", GoogleMap)
}
